<template>
  <div class="stmp-chat-panel">
    <div class="row g-3">
      <!-- Lista konwersacji - pełna szerokość -->
      <div class="col-12" v-if="!selected">
        <div class="card h-100">
          <div class="card-header d-flex justify-content-between align-items-center">
            <div>
              <div class="fw-semibold">Czat</div>
              <small class="text-muted">Wybierz konwersację</small>
            </div>
            <a href="#" class="small" @click.prevent="loadConversations">Odśwież</a>
          </div>
          <div class="list-group list-group-flush" style="max-height: 520px; overflow: auto;">
            <div v-if="!loading && conversations.length === 0" class="p-3 text-center text-muted">Brak konwersacji</div>
            <a
              v-for="c in conversations"
              :key="c.id"
              href="#"
              @click.prevent="selectConversation(c)"
              class="list-group-item list-group-item-action d-flex align-items-start gap-2"
            >
              <div class="position-relative me-2">
                <div class="avatar-circle">{{ (c.buyerName?.charAt(0) || 'U').toUpperCase() }}</div>
                <span v-if="c.isOnline" class="status-dot"></span>
              </div>
              <div class="flex-grow-1 overflow-hidden">
                <div class="d-flex justify-content-between align-items-center mb-1">
                  <div class="fw-semibold text-truncate" :title="c.offerTitle">{{ c.offerTitle }}</div>
                  <div class="d-flex align-items-center gap-2 ms-2 flex-shrink-0">
                    <small class="text-muted">{{ formatTime(c.timestamp) }}</small>
                    <span v-if="c.unreadCount > 0" class="badge bg-danger rounded-pill">{{ c.unreadCount > 9 ? '9+' : c.unreadCount }}</span>
                  </div>
                </div>
                <div class="small text-muted text-truncate">{{ c.buyerName }} • {{ c.buyerId }}</div>
                <div class="small text-truncate">{{ c.lastMessage }}</div>
              </div>
            </a>
          </div>
        </div>
      </div>

      <div class="col-12" v-else>
        <div class="h-100 min-h-[450px]">
          <div class="min-h-[450px]">
            <a href="#" class="btn btn-link p-0 mb-2" @click.prevent="goBackToList">← Wróć do listy</a>
            <ChatWidget :key="selected.id" :offerId="selected.offerId" :buyerId="selected.buyerId" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref } from 'vue';
import { useApiConfig, postJson } from '../composables/useApi';
import ChatWidget from './ChatWidget.vue';

interface ConversationDto {
  id: string;
  offerId: number;
  offerTitle: string;
  buyerId: number;
  buyerName: string;
  lastMessage: string;
  timestamp: string;
  unreadCount: number;
  isOnline: boolean;
}

const cfg = useApiConfig();
const conversations = ref<ConversationDto[]>([]);
const loading = ref(false);
const selected = ref<{ id: string; offerId: number; buyerId: number } | null>(null);
const role = ref<'seller' | 'buyer' | null>(null);

function formatTime(ts: string) {
  try {
    const d = new Date(ts);
    return d.toLocaleString();
  } catch {
    return ts;
  }
}

async function loadConversations(forceRole?: 'seller' | 'buyer') {
  if (!cfg.apiChatConversations) return;
  loading.value = true;
  try {
    const reqRole = forceRole || role.value || 'seller';
    const res = await postJson<{ success: boolean; data: { conversations: ConversationDto[] } }>(
      cfg.apiChatConversations,
      { role: reqRole }
    );
    conversations.value = res?.data?.conversations || [];
    role.value = reqRole;

    // If we have URL params pointing to specific conversation, try to preselect
    const params = new URLSearchParams(window.location.search);
    const off = parseInt(params.get('offerId') || '0', 10);
    const buyer = parseInt(params.get('buyerId') || '0', 10);
    if (!selected.value) {
      if (off > 0 && buyer > 0) {
        const found = conversations.value.find(c => c.offerId === off && c.buyerId === buyer);
        if (found) {
          selected.value = { id: found.id, offerId: found.offerId, buyerId: found.buyerId };
          // keep URL consistent
          try { history.replaceState(null, '', updateQuery({ offerId: String(found.offerId), buyerId: String(found.buyerId) })); } catch {}
        }
      } else if (off > 0) {
        const found = conversations.value.find(c => c.offerId === off);
        if (found) {
          selected.value = { id: found.id, offerId: found.offerId, buyerId: found.buyerId };
          try { history.replaceState(null, '', updateQuery({ offerId: String(found.offerId), buyerId: String(found.buyerId) })); } catch {}
        } else {
          // No conversation in list (e.g. buyer starting new chat) – open chat directly for this offer
          selected.value = { id: `offer-${off}`, offerId: off, buyerId: buyer > 0 ? buyer : 0 };
          try { history.replaceState(null, '', updateQuery({ offerId: String(off), buyerId: buyer > 0 ? String(buyer) : null })); } catch {}
        }
      }
    }

    // If nothing found and we tried seller first without explicit forceRole, fall back to buyer mode
    if ((!res?.data?.conversations || res.data.conversations.length === 0) && (forceRole === undefined) && reqRole === 'seller') {
      await loadConversations('buyer');
      return;
    }
  } catch (e) {
    console.error(e);
  } finally {
    loading.value = false;
  }
}

function selectConversation(c: ConversationDto) {
  selected.value = { id: c.id, offerId: c.offerId, buyerId: c.buyerId };
  // After ChatWidget loads history (which marks as read on the server), refresh the list
  window.setTimeout(() => { loadConversations(role.value || undefined); }, 800);
  // update URL
  try { history.replaceState(null, '', updateQuery({ offerId: String(c.offerId), buyerId: String(c.buyerId) })); } catch {}
}

onMounted(() => {
  loadConversations();
});

function goBackToList() {
  selected.value = null;
  // clear buyerId, keep offerId only if needed; here we remove both
  try { history.replaceState(null, '', updateQuery({ offerId: null, buyerId: null })); } catch {}
  // refresh list to ensure unread counters reflect back
  window.setTimeout(() => { loadConversations(role.value || undefined); }, 200);
}

function updateQuery(params: { offerId?: string | null; buyerId?: string | null }) {
  const url = new URL(window.location.href);
  if (params.offerId === null) url.searchParams.delete('offerId');
  else if (typeof params.offerId === 'string') url.searchParams.set('offerId', params.offerId);
  if (params.buyerId === null) url.searchParams.delete('buyerId');
  else if (typeof params.buyerId === 'string') url.searchParams.set('buyerId', params.buyerId);
  return url.pathname + (url.searchParams.toString() ? `?${url.searchParams.toString()}` : '');
}
</script>

<style scoped>
.stmp-chat-panel .avatar-circle {
  width: 40px; height: 40px; border-radius: 50%;
  background-color: #e9f2ff; color: #0d6efd;
  display: inline-flex; align-items: center; justify-content: center;
  font-weight: 600; font-size: 0.95rem;
}
.stmp-chat-panel .status-dot {
  position: absolute; right: -2px; bottom: -2px; width: 10px; height: 10px;
  background: #28a745; border: 2px solid #fff; border-radius: 50%;
}
.stmp-chat-panel .min-h-480 { min-height: 480px; }
</style>
